import { Collection, Database, Q } from '@nozbe/watermelondb';

import { UserAnswerModel, UserAnswerPayload } from '../../types/userAnswer';
import { DBServiceOptionsWithImages } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    PreparedUserAnswerData,
} from './helpers/userAnswer/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddUserAnswerAction,
    logBatchAddUserAnswerAction,
    prepareCreateUserAnswer,
} from './helpers/userAnswer/userAnswerAdd';
import {
    logDeleteUserAnswerAction,
    prepareDeleteUserAnswer,
} from './helpers/userAnswer/userAnswerDelete';

type UserAnswerServiceOptions = DBServiceOptionsWithImages & {
    validation: DatabaseValidation;
};

class UserAnswer {
    private readonly database: Database;
    private collection: Collection<UserAnswerModel>;
    private table = 'user_answers';
    private options: UserAnswerServiceOptions;

    constructor(options: UserAnswerServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByQuestionID(questionID: string, userID: string) {
        return this.collection
            .query(
                Q.experimentalJoinTables(['answers']),
                Q.and(
                    Q.where('user_id', userID),
                    Q.on('answers', Q.where('question_id', questionID)),
                ),
            )
            .fetch();
    }

    async add(payload: UserAnswerPayload, userID: string) {
        return await this.database.write(async () => {
            const preparedData: PreparedUserAnswerData[] = [];
            const createdUserAnswer = prepareCreateUserAnswer({
                payload,
                userID,
                collection: this.collection,
            });
            preparedData.push(
                mapRecord(createdUserAnswer, 'user_answers', 'add'),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddUserAnswerAction({
                userAnswer: createdUserAnswer,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });

            return createdUserAnswer;
        });
    }

    async batchAdd(batch: UserAnswerPayload[], userID: string) {
        return await this.database.write(async () => {
            const preparedAnswers = batch.map((answer) =>
                prepareCreateUserAnswer({
                    payload: answer,
                    userID,
                    collection: this.collection,
                }),
            );
            const preparedData = mapRecords(
                preparedAnswers,
                'user_answers',
                'add',
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logBatchAddUserAnswerAction({
                userAnswers: preparedAnswers,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }

    async deleteAll() {
        await this.database.write(async () => {
            const userAnswers = await this.collection.query().fetch();
            const toBeDeleted = userAnswers.map((answer) =>
                prepareDeleteUserAnswer({ userAnswer: answer }),
            );
            const preparedData = mapRecords(
                toBeDeleted,
                'user_answers',
                'delete',
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);
        });
    }

    async batchDelete(answers: UserAnswerPayload[], userID: string) {
        await this.database.write(async () => {
            const userAnswers = await this.collection
                .query(
                    Q.and(
                        Q.where('user_id', userID),
                        Q.or(
                            ...answers.map((answer) =>
                                Q.where('answer_id', answer.answerId),
                            ),
                        ),
                    ),
                )
                .fetch();

            const toBeDeleted = userAnswers.map((userAnswers) =>
                prepareDeleteUserAnswer({ userAnswer: userAnswers }),
            );
            const preparedData = mapRecords(
                toBeDeleted,
                'user_answers',
                'delete',
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteUserAnswerAction({
                userAnswers,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default UserAnswer;
